/**
 * Item Update Store
 * 
 * Manages item update notifications with Supabase Realtime subscriptions.
 * Provides real-time update notifications across all users in a game.
 */

import { supabase } from '@/lib/config/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

// Item update type from the database
export interface ItemUpdate {
  id: string;
  item_id: string;
  item_type: string;
  game_id: string;
  updated_by: string;
  action: 'created' | 'updated';
  updated_at: string;
  // Profile data from join
  profiles?: {
    id: string;
    full_name?: string;
    username?: string;
    email?: string;
    avatar_url?: string;
  };
}

// Callback type for update notifications
type UpdateCallback = (update: ItemUpdate) => void;

class ItemUpdateStore {
  private subscribers: Map<string, Set<UpdateCallback>> = new Map();
  private realtimeChannel: RealtimeChannel | null = null;
  private currentGameId: string | null = null;
  private currentUserId: string | null = null;

  /**
   * Initialize the store with game and user context
   */
  async initialize(gameId: string, userId: string): Promise<void> {
    // If already initialized for this game, skip
    if (this.currentGameId === gameId && this.realtimeChannel) {
      return;
    }

    // Clean up existing subscription
    await this.cleanup();

    this.currentGameId = gameId;
    this.currentUserId = userId;

    // Set up realtime subscription
    this.setupRealtimeSubscription();
  }

  /**
   * Set up Supabase Realtime subscription for update events
   */
  private setupRealtimeSubscription(): void {
    if (!this.currentGameId) return;

    // Create channel for item_updates table
    this.realtimeChannel = supabase
      .channel(`item_updates:${this.currentGameId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'item_updates',
          filter: `game_id=eq.${this.currentGameId}`,
        },
        (payload) => {
          this.handleRealtimeEvent(payload);
        }
      )
     
  }

  /**
   * Handle realtime events from Supabase
   */
  private handleRealtimeEvent(payload: any): void {
    const { eventType, new: newRecord } = payload;


    if (eventType === 'INSERT' && newRecord) {
      const update = newRecord as ItemUpdate;
      
      // Don't notify the user who made the update
      if (update.updated_by === this.currentUserId) {
        return;
      }

      this.notifyAllSubscribers(update);
    }
  }

  /**
   * Get subscriber key for map storage
   */
  private getSubscriberKey(itemId: string, itemType: string): string {
    return `${itemType}:${itemId}`;
  }

  /**
   * Subscribe to updates for a specific item
   */
  subscribe(itemId: string, itemType: string, callback: UpdateCallback): () => void {
    const key = this.getSubscriberKey(itemId, itemType);
    
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    
    this.subscribers.get(key)!.add(callback);
    
    return () => {
      const callbacks = this.subscribers.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscribers.delete(key);
        }
      }
    };
  }

  /**
   * Subscribe to all created events for an item type (for list views)
   */
  subscribeToCreated(itemType: string, callback: UpdateCallback): () => void {
    const key = `created:${itemType}`;
    
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    
    this.subscribers.get(key)!.add(callback);
    
    // Also set up a listener for all updates of this type
    if (!this.realtimeChannel) {
      console.warn('[ItemUpdateStore] No realtime channel active');
      return () => {};
    }

    return () => {
      const callbacks = this.subscribers.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscribers.delete(key);
        }
      }
    };
  }

  /**
   * Notify subscribers listening for created events
   */
  private notifyCreatedSubscribers(update: ItemUpdate): void {
    if (update.action !== 'created') return;

    const key = `created:${update.item_type}`;
    const callbacks = this.subscribers.get(key);

    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(update);
        } catch (error) {
          console.error('[ItemUpdateStore] Subscriber error:', error);
        }
      });
    }
  }

  /**
   * Override notifySubscribers to also notify created listeners
   */
  private notifyAllSubscribers(update: ItemUpdate): void {
    // Notify specific item subscribers
    const key = this.getSubscriberKey(update.item_id, update.item_type);
    const callbacks = this.subscribers.get(key);

    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(update);
        } catch (error) {
          console.error('[ItemUpdateStore] Subscriber error:', error);
        }
      });
    }

    // Also notify created event subscribers
    this.notifyCreatedSubscribers(update);
  }

  /**
   * Cleanup subscriptions and state
   */
  async cleanup(): Promise<void> {
    if (this.realtimeChannel) {
      await supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
    this.subscribers.clear();
    this.currentGameId = null;
    this.currentUserId = null;
  }
}

// Singleton instance
export const itemUpdateStore = new ItemUpdateStore();
export default itemUpdateStore;
